import { LockClosedIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';

import {
  CANNOT_CONFIRM_NOTE,
  CONFIRM_IDENTITY_TITLE,
  confirmationAsk,
  confirmationErrorMessage,
  confirmationLede,
  confirmationRefusedField,
  OTHER_TWO_FACTOR_METHOD,
  passwordProofFrom,
  passwordProofReady,
} from '@personal-trainer-app/shared';

import { TwoFactorCodeField } from '../TwoFactorCodeField';
import { TwoFactorMethodSwitch } from '../TwoFactorMethodSwitch';

import type { ConfirmIdentityModalProps } from './ConfirmIdentityModal.types';
import type { FormInputProps } from '@/components/forms';
import type {
  SocialProvider,
  TwoFactorSignInMethod,
} from '@personal-trainer-app/shared';

import { SocialOAuthButtons } from '@/components/auth/SocialOAuthButtons';
import { FormInput } from '@/components/forms';
import { Button, IconRow, Text } from '@/components/ui';
import { Modal } from '@/components/ui/Modal';

const PASSWORD_FIELD = {
  type: 'password',
  label: 'Password',
  autoComplete: 'current-password',
} as const satisfies Partial<FormInputProps>;

/**
 * ConfirmIdentityModal — "Confirm it's you", asked when a change to how the
 * account is signed in to needs a confirmation from the last ten minutes (a
 * 423). It asks for one thing, the proof this account has (shared
 * `confirmationAsk`, in the words of `confirmationLede`, as mobile asks):
 *
 * - its password, when it has one, even with a provider connected too. That
 *   confirms in place, and the change carries on. With two-factor on, a code
 *   from the authenticator app (or a recovery code) comes with it, and a
 *   refusal is said under the field the API named: a refused code keeps the
 *   password;
 * - otherwise a fresh sign-in with its provider (an account has one at most),
 *   while the social sign-in switch is on. That leaves the page and comes back
 *   to Settings, where the change resumes.
 *
 * With neither, it says so rather than showing an empty form. Opened by
 * `IdentityConfirmationProvider`, never directly.
 */
export function ConfirmIdentityModal({
  isOpen,
  changeLabel,
  methods,
  twoFactor = false,
  showProviders = false,
  onConfirmPassword,
  onConfirmWithProvider,
  onClose,
}: ConfirmIdentityModalProps): React.JSX.Element {
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  // With two-factor on: which second factor is asked for, and what's typed.
  const [method, setMethod] = useState<TwoFactorSignInMethod>('code');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  // A provider was chosen: the page is leaving for it.
  const [leaving, setLeaving] = useState(false);

  // Each time it opens, it asks afresh.
  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setPasswordError(null);
      setMethod('code');
      setCode('');
      setCodeError(null);
      setConfirming(false);
      setLeaving(false);
    }
  }, [isOpen]);

  const ask = confirmationAsk(
    methods,
    showProviders && onConfirmWithProvider !== undefined,
    { twoFactor }
  );
  const lede = confirmationLede(ask);
  const hasPassword = ask.proof === 'password';
  const asksCode = ask.proof === 'password' && ask.twoFactor === true;
  const busy = confirming || leaving;
  const typed = { password, code, method, twoFactor: asksCode };
  const canConfirm = passwordProofReady(typed) && !busy;

  const handleClose = (): void => {
    if (!busy) onClose();
  };

  const handleProvider = (chosen: SocialProvider): void => {
    if (busy || !onConfirmWithProvider) return;
    setLeaving(true);
    onConfirmWithProvider(chosen);
  };

  const switchMethod = (): void => {
    setMethod((current) => OTHER_TWO_FACTOR_METHOD[current]);
    setCode('');
    setCodeError(null);
  };

  const handleConfirm = async (): Promise<void> => {
    if (!canConfirm) return;
    const read = passwordProofFrom(typed);
    if ('problem' in read) {
      setCodeError(read.problem);
      return;
    }
    setConfirming(true);
    setPasswordError(null);
    setCodeError(null);
    try {
      await onConfirmPassword(read.proof);
      // Confirmed: the provider closes the modal and the change carries on.
    } catch (refusal) {
      const message = confirmationErrorMessage(refusal);
      if (asksCode && confirmationRefusedField(refusal) !== 'password') {
        setCodeError(message);
      } else {
        setPasswordError(message);
      }
      setConfirming(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      title={CONFIRM_IDENTITY_TITLE}
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={busy}>
            {ask.proof === 'none' ? 'Close' : 'Cancel'}
          </Button>
          {hasPassword ? (
            <Button
              variant="primary"
              onClick={() => void handleConfirm()}
              loading={confirming}
              disabled={!canConfirm}
            >
              Confirm
            </Button>
          ) : null}
        </>
      }
    >
      <Text variant="body-md" color="secondary">
        {lede.before}
        <span className="font-medium text-neutral-50">{changeLabel}</span>
        {lede.after}
      </Text>

      {hasPassword ? (
        <div className="mt-5 space-y-4">
          <FormInput
            {...PASSWORD_FIELD}
            value={password}
            error={passwordError ?? undefined}
            disabled={busy}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleConfirm();
            }}
          />
          {asksCode ? (
            <div>
              <TwoFactorCodeField
                method={method}
                value={code}
                onValueChange={(typed) => {
                  setCode(typed);
                  if (codeError) setCodeError(null);
                }}
                error={codeError}
                // The other method's field takes focus as it swaps in. On
                // open, Modal puts it on the first field, the password.
                autoFocus
                disabled={busy}
                onEnter={() => void handleConfirm()}
              />
              <TwoFactorMethodSwitch
                method={method}
                onSwitch={switchMethod}
                disabled={busy}
                className="mt-2"
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {ask.proof === 'provider' ? (
        <SocialOAuthButtons
          providers={[ask.provider]}
          wording="sign-in"
          showDivider={false}
          disabled={busy}
          onProviderClick={handleProvider}
          className="mt-5"
        />
      ) : null}

      {ask.proof === 'none' ? (
        <IconRow icon={<LockClosedIcon className="h-4 w-4" />} className="mt-3">
          {CANNOT_CONFIRM_NOTE}
        </IconRow>
      ) : null}
    </Modal>
  );
}

export default ConfirmIdentityModal;
